import type { FogBugzCase } from "@/hooks/queries/use-fogbugz";
import { Badge } from "@/components/ui/badge";
import { getProjectCounts } from "@/pages/fogbugz/utils/group-cases";
import { cn } from "@/lib/utils";

interface ProjectFilterBarProps {
  cases: FogBugzCase[];
  selectedProject: string | null;
  onSelectProject: (project: string | null) => void;
}

export function ProjectFilterBar({
  cases,
  selectedProject,
  onSelectProject,
}: ProjectFilterBarProps) {
  const projectCounts = getProjectCounts(cases);

  if (projectCounts.size === 0) return null;

  const totalCount = cases.length;

  const handleChipClick = (project: string | null) => {
    onSelectProject(selectedProject === project ? null : project);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by project">
      {/* "All" chip */}
      <button
        type="button"
        onClick={() => handleChipClick(null)}
        aria-pressed={selectedProject === null}
        aria-label={`Show tickets from all projects (${totalCount})`}
        className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Badge
          variant={selectedProject === null ? "default" : "outline"}
          className={cn("cursor-pointer select-none", selectedProject !== null && "hover:bg-muted")}
        >
          All ({totalCount})
        </Badge>
      </button>

      {[...projectCounts.entries()].map(([project, count]) => {
        const isSelected = selectedProject === project;
        return (
          <button
            key={project}
            type="button"
            onClick={() => handleChipClick(project)}
            aria-pressed={isSelected}
            aria-label={`Filter by project ${project} (${count})`}
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Badge
              variant={isSelected ? "default" : "outline"}
              className={cn("cursor-pointer select-none", !isSelected && "hover:bg-muted")}
            >
              {project} ({count})
            </Badge>
          </button>
        );
      })}
    </div>
  );
}
